import { Link } from "react-router-dom";
import { CheckCircle2, FileCheck2, Coffee, BadgeCheck } from "lucide-react";
import { COFFEE_BEANS } from "@/lib/company";

const heroBg = "https://customer-assets-0z36b82j.emergentagent.net/job_aad-labs-export/artifacts/jxuptai3_robusta.png";

export default function CoffeeBeans() {
  return (
    <div data-testid="coffee-beans-page">
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="Coffee beans" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(30,20,10,0.75) 0%, rgba(45,30,15,0.65) 60%, rgba(20,15,10,0.9) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-5">Category D</span>
          <h1 className="font-display font-bold text-white text-3xl md:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Indian Coffee Beans —<br />
            <span className="text-brand-gold">Shade-Grown, Estate-Processed, Export Grade</span>
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-3xl">
            From the misty slopes of the Western Ghats to specialty roasters and espresso houses across Europe, the Middle East, and Asia — we supply green, unroasted Arabica and Robusta coffee to a discerning global trade.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-brand-charcoal/85 leading-relaxed text-lg">
            India is one of the world's oldest coffee-growing nations — cultivating both Arabica and Robusta beneath the natural shade of rainforest canopy in Karnataka, Kerala, and Tamil Nadu. Our estate partners hold <strong>Rainforest Alliance</strong>, <strong>UTZ</strong>, <strong>Fair Trade</strong>, and <strong>Organic</strong> certifications, and every lot ships with a batch-specific Certificate of Analysis covering moisture, defect count, screen size, and cupping profile. Whether you roast at 15 kg or import at container-load volumes, we curate the origin, processing method, and grade that matches your cup.
          </p>
        </div>
      </section>

      {/* Product cards */}
      <section className="py-16 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 space-y-10">
          {COFFEE_BEANS.map((p, idx) => (
            <div
              key={p.slug}
              data-testid={`coffee-detail-${p.slug}`}
              className={`bg-white rounded-lg shadow-md border border-neutral-200 overflow-hidden grid lg:grid-cols-2 gap-0 ${idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden bg-neutral-100">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 flex flex-col">
                <div className="flex items-center gap-2 text-brand-gold text-sm md:text-base font-accent italic">
                  <Coffee className="w-4 h-4" /> {p.tagline}
                </div>
                <h2 className="mt-2 font-display font-bold text-brand-charcoal text-3xl">{p.name}</h2>
                <div className="mt-1 text-brand-charcoal/60 text-sm italic">{p.botanical}</div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <MiniBadge label="Origin" value={p.origin} />
                  <MiniBadge label="Grade" value={p.grade} />
                  <MiniBadge label="Processing" value={p.processing} />
                  <MiniBadge label="Roast Profile" value={p.roastProfile} />
                </div>

                <p className="mt-5 text-brand-charcoal/80 leading-relaxed text-sm">{p.description}</p>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {p.specs.map((s) => (
                    <div key={s.k} className="flex items-start gap-2 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-brand-green mt-0.5 shrink-0" />
                      <div>
                        <span className="font-semibold text-brand-charcoal">{s.k}:</span>{" "}
                        <span className="text-brand-charcoal/75">{s.v}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 space-y-3 text-sm">
                  <div>
                    <span className="text-xs uppercase tracking-widest text-brand-gold font-bold">Cupping Profile</span>
                    <div className="mt-1 text-brand-charcoal/85 italic">{p.cupping}</div>
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-brand-gold font-bold">Applications</span>
                    <div className="mt-1 text-brand-charcoal/85">{p.applications}</div>
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-brand-gold font-bold">Packaging</span>
                    <div className="mt-1 text-brand-charcoal/85">{p.packaging}</div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/contact" data-testid={`coffee-spec-${p.slug}`} className="btn-green" style={{ padding: "0.7rem 1.2rem", fontSize: "0.9rem" }}>
                    Request Spec Sheet
                  </Link>
                  <Link to="/contact" data-testid={`coffee-sample-${p.slug}`} className="btn-outline-green" style={{ padding: "0.7rem 1.2rem", fontSize: "0.9rem" }}>
                    Order Sample
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications strip */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Coffee Certifications</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline-center">
              Our Estate Partners Hold
            </h2>
            <p className="mt-5 text-brand-charcoal/70 max-w-2xl mx-auto">
              Every lot is traceable to an origin estate. Our partner farms are independently audited and certified against the highest global sustainability, ethics, and food-safety standards.
            </p>
          </div>

          <div className="space-y-14">
            {[
              {
                title: "Sustainability & Ethical Sourcing",
                items: ["Rainforest Alliance", "UTZ Certified", "Fair Trade", "4C Common Code"],
              },
              {
                title: "Organic & Origin",
                items: ["USDA Organic", "EU Organic", "India Organic", "Coffee Board of India"],
              },
              {
                title: "Food Safety & Export",
                items: ["FSSAI Compliant", "SHEFEXIL Member", "ICO Certified Origin", "Phytosanitary Certified"],
              },
            ].map((g, gi) => (
              <div key={gi}>
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Group {gi + 1}</span>
                <h3 className="mt-2 font-display font-bold text-brand-charcoal text-2xl md:text-3xl gold-underline">{g.title}</h3>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {g.items.map((c) => (
                    <div
                      key={c}
                      data-testid={`coffee-cert-${c.toLowerCase().replace(/\s+/g, "-").replace(/[()\/]/g, "")}`}
                      className="flex items-center gap-3 p-5 bg-brand-cream border border-neutral-200 rounded-md hover:border-brand-gold transition-colors"
                    >
                      <BadgeCheck className="w-6 h-6 text-brand-green shrink-0" />
                      <span className="font-semibold text-brand-charcoal">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Docs strip */}
      <section className="py-20 bg-brand-green text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Every Shipment Includes</span>
            <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl gold-underline-center">Full Coffee Export Documentation</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Batch-specific Certificate of Analysis (moisture, screen, defects, cupping)",
              "Coffee Board of India permit & origin certificate",
              "Phytosanitary Certificate",
              "ICO Certificate of Origin",
              "Commercial invoice & packing list",
              "Organic / Fair Trade / Rainforest Alliance certificates (where applicable)",
            ].map((d) => (
              <div key={d} className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-md border border-white/15">
                <FileCheck2 className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
                <span className="text-sm">{d}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/contact" data-testid="coffee-cta" className="btn-gold">
              Request Coffee Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function MiniBadge({ label, value }) {
  return (
    <div className="bg-brand-cream border border-neutral-200 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-brand-gold font-bold">{label}</div>
      <div className="text-xs text-brand-charcoal mt-0.5 font-semibold leading-snug">{value}</div>
    </div>
  );
}
